/**
 * PROTOTYPE state + pricing for the "split a proposal into spreads" design -- THROWAWAY.
 *
 * Pricing model being validated:
 *   - Each spread is its own complete mini-project.
 *   - Project total = simple SUM of all spreads. Nothing shared or de-duplicated.
 *   - Platform + retention therefore sit INSIDE each spread, not once at project level.
 *   - MT / NDT / Coating volumes default to the spread's joint count, editable per module.
 *   - Each spread carries the FULL config of every module (see spreadConfigDefs).
 *
 * Numbers here are placeholder rates, not the real pricing engine.
 */
import { MODULE_DEFAULTS } from './spreadConfigDefs';
import { MtOptions } from './spreadMaterialData';
import { ModuleSource, sourceCount } from './spreadSourceData';
import { PlatformOptions, RetentionOptions } from './spreadProjectOptions';

export type MachineKey = 'p625' | 'p600z' | 'm500' | 'm300' | 'manual' | 'iwm';
export type ModuleKey = 'welding' | 'mt' | 'ndt' | 'coating' | 'platform' | 'retention';

export const MACHINE_KEYS: MachineKey[] = ['p625', 'p600z', 'm500', 'm300', 'manual', 'iwm'];
export const MACHINE_LABELS: Record<MachineKey, string> = {
  p625: 'P-625', p600z: 'P-600z', m500: 'M-500', m300: 'M-300', manual: 'Manual', iwm: 'IWM',
};

export const MODULE_LABELS: Record<ModuleKey, string> = {
  welding: 'Welding', mt: 'Material Traceability', ndt: 'NDT',
  coating: 'Coating', platform: 'Platform', retention: 'Retention',
};

export const ALL_MODULE_KEYS: ModuleKey[] = ['welding', 'mt', 'ndt', 'coating', 'platform', 'retention'];
/** Which modules a spread includes. Disabled modules are not priced and drop out of the
 *  platform module count. Every module starts enabled. */
export const DEFAULT_ENABLED: Record<ModuleKey, boolean> = {
  welding: true, mt: true, ndt: true, coating: true, platform: true, retention: true,
};

export type DerivedKey = 'mt' | 'ndt' | 'coating';
export const DERIVED_KEYS: DerivedKey[] = ['mt', 'ndt', 'coating'];

// Geometry: joints are derived from spread length / pipe joint length.
export const FEET_PER_MILE = 5280;
export const FEET_PER_KM = 3280.84;
export const PIPE_LENGTH_OPTIONS = [40, 80]; // ft (single / double random length); custom allowed
export type LengthUnit = 'mi' | 'km';
export type ThroughputMode = 'perDay' | 'duration';
const WORKING_DAYS_PER_MONTH = 21;

// Placeholder rates (USD).
const MACHINE_RATES: Record<MachineKey, number> = { p625: 18500, p600z: 16000, m500: 12000, m300: 9500, manual: 4500, iwm: 9000 };
const MODULE_RATE: Record<DerivedKey, number> = { mt: 7.1, ndt: 5.2, coating: 4.7 }; // per joint
const PLATFORM_PER_SPREAD = 12000;   // each spread = own project
const RETENTION_PER_SPREAD = 3000;

// Placeholder margin for the two modules whose config has no margin field.
const WELDING_MARGIN = 0.35;    // welding margin is embedded in the rate build-up
const RETENTION_MARGIN = 0.6;
const MOBILIZATION = 15000;     // welding, per spread (placeholder; real value derives from shipping)

/** Per-spread overrides: the full module config, keyed by module then field. */
export interface SpreadOverrides {
  config?: Partial<Record<ModuleKey, Record<string, number>>>;
}

// Config layers: constant default -> live engine config -> project rate card -> spread override.
export function moduleConstDefault(module: ModuleKey, key: string): number {
  return MODULE_DEFAULTS[module]?.[key] ?? 0;
}
/** Project-level value a spread inherits: project rate card, else the live engine config
 *  (loaded from the backend so the drawer shows the engine's real defaults), else the constant. */
export function projectCfg(project: ProjectState, module: ModuleKey, key: string): number {
  return project.config?.[module]?.[key] ?? project.liveDefaults?.[module]?.[key] ?? moduleConstDefault(module, key);
}
export function projectCfgOverridden(project: ProjectState, module: ModuleKey, key: string): boolean {
  return project.config?.[module]?.[key] != null;
}
/** Effective value for a spread: its override, else the value it inherits from the project. */
export function moduleCfg(project: ProjectState, s: Spread, module: ModuleKey, key: string): number {
  return s.overrides.config?.[module]?.[key] ?? projectCfg(project, module, key);
}
export function moduleCfgOverridden(s: Spread, module: ModuleKey, key: string): boolean {
  return s.overrides.config?.[module]?.[key] != null;
}

export interface Spread {
  id: string;
  name: string;
  // Geometry -> joints
  lengthValue: number;
  lengthUnit: LengthUnit;
  pipeLengthFt: number;
  jointsOverride: number | null;   // null = use computed joints
  // Timeline
  throughputMode: ThroughputMode;
  jointsPerDay: number;
  durationMonths: number;          // used directly when throughputMode === 'duration'
  // Config
  machines: Record<MachineKey, number>;
  // Data sources per volume-driven module (+ MT's flat add-on toggles).
  mtMills: ModuleSource[];        // MT mills
  mtOptions: MtOptions;
  ndtSources: ModuleSource[];     // NDT vendors
  coatingSources: ModuleSource[]; // Coating contractors
  // Project-level inputs (platform users / infra / support, retention tier + years).
  platformOptions: PlatformOptions;
  retentionOptions: RetentionOptions;
  enabled: Record<ModuleKey, boolean>; // which modules this spread includes
  overrides: SpreadOverrides;
}

export function lengthFeet(s: Spread): number {
  return s.lengthValue * (s.lengthUnit === 'mi' ? FEET_PER_MILE : FEET_PER_KM);
}
export function computedJoints(s: Spread): number {
  return Math.max(0, Math.round(lengthFeet(s) / (s.pipeLengthFt || 1)));
}
export function joints(s: Spread): number {
  return s.jointsOverride ?? computedJoints(s);
}
export function jointsOverridden(s: Spread): boolean {
  return s.jointsOverride != null;
}
export function workingDays(s: Spread): number {
  return s.jointsPerDay > 0 ? Math.ceil(joints(s) / s.jointsPerDay) : 0;
}
/** Duration in months: entered directly, or derived from throughput. */
export function durationMonthsFor(s: Spread): number {
  if (s.throughputMode === 'duration') return s.durationMonths;
  return Math.round((workingDays(s) / WORKING_DAYS_PER_MONTH) * 10) / 10;
}

/** Effective margin fraction. mt/ndt/coating/platform read marginTarget from config; welding/retention are placeholders. */
export function effMargin(project: ProjectState, s: Spread, m: ModuleKey): number {
  if (m === 'welding') return WELDING_MARGIN;
  if (m === 'retention') return RETENTION_MARGIN;
  return moduleCfg(project, s, m, 'marginTarget') / 100;
}

export type ReportingLevel = 'project' | 'spread';

// Project-level welding services (billed ONCE for the whole project).
export type TrainingTier = 'none' | 'quick' | 'medium' | 'long';
export interface WeldingServices { enabled: boolean; supportHours: number; trainingTier: TrainingTier; provisioningHours: number; onFieldDays: number; }
export const DEFAULT_SERVICES: WeldingServices = { enabled: false, supportHours: 0, trainingTier: 'none', provisioningHours: 0, onFieldDays: 0 };
const TRAINING_HOURS_KEY: Record<Exclude<TrainingTier, 'none'>, string> = {
  quick: 'trainingQuickHours', medium: 'trainingMediumHours', long: 'trainingLongHours',
};

export interface ProjectState {
  customerName: string;
  projectTitle: string;
  refNumber: string;
  reporting: ReportingLevel;
  spreads: Spread[];
  config?: Partial<Record<ModuleKey, Record<string, number>>>;       // project rate-card overrides
  liveDefaults?: Partial<Record<ModuleKey, Record<string, number>>>; // engine config loaded from the backend
  services: WeldingServices;                                          // project-level welding services
}

export interface ModuleLine { price: number; cost: number; margin: number; }
export type SpreadPricing = Record<ModuleKey, ModuleLine> & { total: number };

const SOURCE_FIELD: Record<'mt' | 'ndt' | 'coating', 'mtMills' | 'ndtSources' | 'coatingSources'> = {
  mt: 'mtMills', ndt: 'ndtSources', coating: 'coatingSources',
};

/** A module's data sources (mills / vendors / contractors). */
export function moduleSources(s: Spread, key: 'mt' | 'ndt' | 'coating'): ModuleSource[] {
  return s[SOURCE_FIELD[key]];
}

/** Total records for a volume-driven module: the sum of its sources' record counts. */
export function moduleVolume(s: Spread, key: 'mt' | 'ndt' | 'coating'): number {
  return sourceCount(moduleSources(s, key));
}

/** Total pipes tracked by Material Traceability (kept for MT's summary line). */
export function mtPipeTotal(s: Spread): number {
  return moduleVolume(s, 'mt');
}

function line(price: number, margin: number): ModuleLine {
  return { price, cost: price * (1 - margin), margin };
}

function derivedLine(project: ProjectState, s: Spread, k: DerivedKey): ModuleLine {
  // placeholder: volume x rate, margin read from the module config
  return line(moduleVolume(s, k) * MODULE_RATE[k], effMargin(project, s, k));
}

export function priceSpread(project: ProjectState, s: Spread): SpreadPricing {
  const weldingPrice =
    MACHINE_KEYS.reduce((sum, k) => sum + s.machines[k] * MACHINE_RATES[k], 0) + MOBILIZATION;
  const welding = line(weldingPrice, effMargin(project, s, 'welding'));
  const mt = derivedLine(project, s, 'mt');
  const ndt = derivedLine(project, s, 'ndt');
  const coating = derivedLine(project, s, 'coating');
  const platform = line(PLATFORM_PER_SPREAD, effMargin(project, s, 'platform'));
  const retention = line(RETENTION_PER_SPREAD, effMargin(project, s, 'retention'));
  const total = welding.price + mt.price + ndt.price + coating.price + platform.price + retention.price;
  return { welding, mt, ndt, coating, platform, retention, total };
}

/** Project-level welding services line (billed once). Uses the project rate card. */
export function weldingServicesPrice(project: ProjectState): ModuleLine {
  const sv = project.services;
  const rate = (k: string) => projectCfg(project, 'welding', k);
  const trainingHours = sv.trainingTier === 'none' ? 0 : rate(TRAINING_HOURS_KEY[sv.trainingTier]);
  const price =
    sv.supportHours * rate('maintenanceAndSupport') +
    trainingHours * rate('trainingRate') +
    sv.provisioningHours * rate('provisioningHourlyRate') +
    sv.onFieldDays * rate('onFieldSupportDailyRate');
  return line(price, WELDING_MARGIN);
}

/** Project grand total: sum of spreads + project-level services (once). */
export function projectTotal(project: ProjectState): number {
  return project.spreads.reduce((sum, s) => sum + priceSpread(project, s).total, 0) + weldingServicesPrice(project).price;
}

export function machineSummary(s: Spread): string {
  return MACHINE_KEYS
    .filter((k) => s.machines[k] > 0)
    .map((k) => `${MACHINE_LABELS[k]} x${s.machines[k]}`)
    .join(', ') || '—';
}

export function money(n: number): string {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}
